'use client'

import srOnly from '@fukku/styles/src/srOnly.module.css'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { getMicrodata } from 'prices/utils/getMicrodata/getMicrodata'
import { getScreenReaderText } from 'prices/utils/getScreenReaderText/getScreenReaderText'
import { cx } from 'utils/cx'
import { usePricesStore } from '../../context/PricesProvider'
import type { Price } from '../../store/pricesStore/pricesStore'
import { getAlignStyle } from '../../utils/getStyles/getStyles'
import styles from './SinglePrice.module.css'

type SinglePriceProps = {
	readonly price: Price
	readonly finalPrice?: boolean
	readonly isCrossedOut?: boolean
	readonly dataTestid?: string
	readonly label: string
	readonly priceClassName?: string
	readonly additionalPriceClassName?: string
	/** when its true (default) prices have large text annotations for Screen Readers (use case: omnibus) */
	readonly withSROnlyAnnotations?: boolean
	readonly isPricesHistory?: boolean
}

export function SinglePrice({
	price,
	finalPrice = false,
	isCrossedOut = false,
	dataTestid,
	label,
	priceClassName,
	additionalPriceClassName,
	withSROnlyAnnotations = true,
	isPricesHistory = false,
}: SinglePriceProps) {
	const {
		additionalPriceVertical = false,
		align = 'start',
		hasMicrodata = false,
		country: {
			currency: { iso: currencyISO },
		},
	} = usePricesStore()

	const additionalPriceVerticalValue =
		!isPricesHistory && additionalPriceVertical

	const { t } = useLabels()
	const screenReaderText = getScreenReaderText({
		label: t(label),
		additionalPrices: price.additionalPrices,
		priceWithCurrency: price.formatted,
	})

	const microdata = getMicrodata(hasMicrodata)

	const alignStyle = getAlignStyle({
		align,
		additionalPriceVertical: additionalPriceVerticalValue,
	})

	return (
		<>
			{withSROnlyAnnotations && (
				<span className={srOnly.className}>{screenReaderText}</span>
			)}
			<span
				className={styles.nowrap}
				aria-hidden={withSROnlyAnnotations ? 'true' : undefined}
				data-testid={dataTestid}
				{...microdata}
			>
				{hasMicrodata && (
					<>
						<meta itemProp='priceCurrency' content={currencyISO} />
						<meta itemProp='price' content={price.amount.toString()} />
					</>
				)}
				<span
					className={cx(
						styles[align ?? 'start'],
						additionalPriceVerticalValue ? styles.allVertical : styles.container
					)}
				>
					<span
						className={cx(
							isCrossedOut && styles.crossed,
							alignStyle,
							priceClassName,
							finalPrice && styles.finalPrice
						)}
						data-testid='singlePrice'
					>
						{price.formatted}
					</span>
					{price.additionalPrices && (
						<span
							className={cx(
								isCrossedOut && styles.crossed,
								alignStyle,
								additionalPriceClassName,
								finalPrice && styles.finalPrice,
								additionalPriceVerticalValue && styles.allVerticalSpacing
							)}
						>
							{price.additionalPrices}
						</span>
					)}
				</span>
			</span>
		</>
	)
}
